import React, { useMemo, useState } from 'react';
import App from '@gral/app';
import {
  Checkbox,
  Grid,
  Button,
  DateTimeInput,
  DateTimeRangeInput,
  onChangeObject,
  Select,
  Tooltip,
  Icon,
  TextInput,
  TTextType,
} from '@ts/storybook';
import { useAppDispatch } from '@redux/store';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import { DATE_RANGE_MODE, EAclAction, IMapOptions, Paths } from '@ts/ui-models';
import { normalizeIMapOptions } from '@components/Maps/normalizers/Map.normalizers';
import { useMapContext } from '@pages/MapPage';
import { useRouterContext } from '@utils/context/RouterContext';
import { IInheritedMapProps } from '@modules/newMap/WrapperMapView';
import useEffectAfterMount from '@utils/hooks/useEffectAfterMount';
import { cloneDeep, map } from 'lodash';

interface SectionTimeLineProps {
  inheritedMapProps: IInheritedMapProps;
}

const SectionTimeLine: React.FC<SectionTimeLineProps> = props => {
  const dispatch = useAppDispatch();
  const mapContext = useMapContext();
  if (!mapContext) return;
  const { view, setView, setCycleState } = mapContext;
  const { isMobile } = useRouterContext();
  const userCanEdit = App.cxStore.checkPermission('taView', EAclAction.EDIT);

  const [state, setState] = useState<{ showBtnSave: boolean; showBtnRefresh: boolean; showDialogTimeline: boolean }>({
    showBtnSave: false,
    showBtnRefresh: false,
    showDialogTimeline: false,
  });
  const [mapOptions, setMapOptions] = useState<IMapOptions>(view.properties);

  const [error, setError] = useState<boolean>(false);

  //#region Handlers
  const notify = (message: TTextType) => {
    dispatch(globalActions.notification(message));
  };
  const errory = (value: boolean) => {
    setError(value);
  };
  const onChange = (attr: Paths<IMapOptions>) => v => {
    onChangeObject(setMapOptions, attr, v, normalizeIMapOptions, notify, errory);
  };
  const onChangeDateRange = (v: Array<string | Date | undefined>) => {
    if (!v[0] || !v[1]) {
      setError(true);
    } else {
      setError(false);
    }
    onChange('dateRange.from')(v[0]);
    onChange('dateRange.to')(v[1]);
  };
  const onClickBtnSave = () => {
    setState({ ...state, showBtnSave: false, showBtnRefresh: false });
    props.inheritedMapProps.onClickBtnSave();
  };
  const onClickBtnRefresh = () => {
    if (state.showBtnRefresh) {
      const updateView = cloneDeep(view);
      updateView.properties = mapOptions;
      setView(updateView);
    }
    setState({ showBtnSave: true, showBtnRefresh: false, showDialogTimeline: false });
    props.inheritedMapProps.onRefreshMap();
    setCycleState(prevState => ({
      ...prevState,
      allTimelinePositionsFetched: 0,
    }));
    notify('Showing the <b>last point</b> of the timeline, click in one item or cluster to see the full timeline.');
  };
  //#endregion

  //#region Function
  const camelCaseToLabel = (str: string): string => {
    return str
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([0-9]+)/g, ' $1')
      .toLowerCase();
  };
  const generateListModeFromEnum = (): { label: string; value: any }[] => {
    const list: string[] = ['lastPosition', ...Object.values(DATE_RANGE_MODE).filter(v => v !== DATE_RANGE_MODE.LAST_YEAR)];
    return list.map(value => ({
      label: camelCaseToLabel(value).replace(/^\w/, c => c.toUpperCase()), // Capitaliza la primera letra
      value,
    }));
  };

  //#endregion

  //#region Render

  const renderDataTimeRange = useMemo(() => {
    if (mapOptions.mode === DATE_RANGE_MODE.DATE_RANGE) {
      return (
        <>
          {mapOptions.now ? (
            <DateTimeInput label="From" value={mapOptions.dateRange?.from} onChange={onChange('dateRange.from')} />
          ) : (
            <DateTimeRangeInput value={[mapOptions.dateRange?.from, mapOptions.dateRange?.to]} onChange={onChangeDateRange} />
          )}
          <Checkbox label="Now" checked={mapOptions.now} onChangeValue={onChange('now')} className="relative top-[5px]" />
        </>
      );
    }
    return null;
  }, [mapOptions, isMobile]);

  const renderBtn = (type: 'refresh' | 'save') => {
    if (!userCanEdit && type === 'save') return;
    const titleTooltip = type === 'refresh' ? 'Refresh map' : 'Save timeline configuration';
    const labelBtn = type === 'refresh' ? 'Refresh' : 'Save';
    const onClickBtn = type === 'refresh' ? onClickBtnRefresh : onClickBtnSave;
    return (
      <Tooltip title={titleTooltip} placement="top">
        <Button
          label={labelBtn}
          onClick={onClickBtn}
          fullWidth
          variant="outlined"
          disabled={error}
          className="capitalize"
          endIcon={<Icon icon={labelBtn} size={20} color={'inherit'} />}
        />
      </Tooltip>
    );
  };

  const renderLastSeconds = () => {
    if (mapOptions.mode === DATE_RANGE_MODE.LAST_SECONDS) {
      return (
        <Grid size={12}>
          <TextInput
            label="Nº of seconds"
            type="number"
            min={5}
            value={mapOptions.lastSeconds}
            onChangeValue={onChange('lastSeconds')}
          />
        </Grid>
      );
    }
  };

  const renderTimeline = () => {
    return (
      <Grid
        container
        size={12}
        spacing={2}
        direction={'column'}
        sx={{
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
        }}
      >
        <Select label="Mode" value={mapOptions.mode ?? 'lastPosition'} onChange={onChange('mode')} options={listMode} />
        <Grid size={11}>
          {renderDataTimeRange}
          {renderLastSeconds()}
        </Grid>
        <Grid size={7}>{(state.showBtnSave || state.showBtnRefresh) && renderBtn(state.showBtnSave ? 'save' : 'refresh')}</Grid>
      </Grid>
    );
  };
  //#endregion

  //We update view to reaload elements position
  useEffectAfterMount(() => {
    setState({ ...state, showBtnSave: false, showBtnRefresh: true });
  }, [mapOptions]);

  const listMode: { label: string; value: DATE_RANGE_MODE }[] = generateListModeFromEnum();

  return <>{renderTimeline()}</>;
};

export default SectionTimeLine;
